import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { City, Province } from '../model/TeacherInfo';
import api from '../utils/api';
import { useLoading } from '../context/LoadingContext';
import { SubjectsAndSchools, TeacherForm } from '../model/registration/TeacherForm';
import { VatType } from '../model/VatType';
import TeacherPersonalInfo from '../components/registration/TeacherPersonalInfo';
import InvoiceInfo from '../components/registration/InvoiceInfo';
import TeacherSujectRegistration from '../components/registration/TeacherSubjectRegistration';
import { validateName, validateCAP, validateItalianPhone } from '../utils/validators';

type Step = 'personalInfo' | 'subjectSelection' | 'invoiceInfo';

const TeacherRegistration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDisabled, setIsDisabled] = useState(true);
  const [step, setStep] = useState<Step>('personalInfo');
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [hideButtons, setHideButtons] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  const [form, setForm] = useState<TeacherForm>({
    token: token || '',
    city_id: 0,
    province_id: 0,
    first_name: '',
    last_name: '',
    subjects: [],
    phone: '',
    cf: '',
    email: '',
    date_of_birth: '',
    password: '',
    street_name: '',
    street_number: '',
    zip: '',
    iban: '',
    destination_id: '',
    terms_and_conditions: 0,
    vat: '',
    vat_type: VatType.FORFETTARIO_STARTUP,
    invoice_province_id: 0,
    invoice_city_id: 0,
    invoice_street_address: '',
    invoice_street_number: '',
    invoice_zip: '',
  });

  const { register } = useAuth();
  const { setLoading } = useLoading();

  useEffect(() => {
    setLoading(true);
    api
      .get('/province?page_size=200')
      .then((response) => {
        setProvinces(response.data.provinces);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (form.province_id) {
      setLoading(true);
      api
        .get(`/city?province_id=${form.province_id}&page_size=200`)
        .then((response) => {
          setCities(response.data.cities);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [form.province_id]);

  const validateFirstName = (firstName: string) => {
    return validateName(firstName, 'Nome');
  };

  const validateLastName = (lastName: string) => {
    return validateName(lastName, 'Cognome');
  };

  const validatePhone = (phone: string) => {
    return validateItalianPhone(phone);
  };

  const validateZip = (zip: string) => {
    return validateCAP(zip);
  };

  const validateCF = (cf: string) => {
    if (!/^[a-zA-Z]{6}\d{2}[a-ehlmpr-tA-EHLMPR-T]{1}\d{2}[a-zA-Z]\d{3}[a-zA-Z]$/.test(cf)) {
      return 'Codice fiscale non valido.';
    }
    return '';
  };

  const validateEmail = (email: string) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Email non valida.';
    }
    return '';
  };

  const validateDateOfBirth = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    if (today.getFullYear() - birthDate.getFullYear() < 18) {
      return 'Devi avere almeno 18 anni.';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password)) {
      return 'Password deve contenere almeno 8 caratteri, una maiuscola, una minuscola e un numero.';
    }
    return '';
  };

  const validateIBAN = (iban: string) => {
    if (!/^[A-Z]{2}\d{2}[A-Z]{1,2}\d{1,30}$/.test(iban)) {
      return 'IBAN non valido.';
    }
    return '';
  };

  const validateDestinationID = (destinationId: string) => {
    if (!/^[A-Za-z0-9]{7}$/.test(destinationId)) {
      return 'Codice destinatario deve essere di 7 cifre (numeri o lettere).';
    }
    return '';
  };

  const validateVAT = (vat: string) => {
    if (!/^\d{11}$/.test(vat)) {
      return 'Partita IVA deve essere composta da 11 cifre numeriche.';
    }
    return '';
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let { name, value } = event.target;

    // Se il campo è 'terms_and_conditions', converti il valore in numero
    if (name === 'terms_and_conditions') {
      value = Number(value) as unknown as string; // Converte a numero, ma mantiene il tipo string per evitare errori
    }

    setForm((prev) => {
      if (name === 'cf') {
        value = value.toUpperCase();
      }
      const newForm = { ...prev, [name]: value };
      let error = '';

      // Solo valida se il campo ha un valore, altrimenti pulisci l'errore
      if (value) {
        switch (name) {
          case 'first_name':
            error = validateFirstName(value);
            break;
          case 'last_name':
            error = validateLastName(value);
            break;
          case 'phone':
            error = validatePhone(value);
            break;
          case 'cf':
            error = validateCF(value);
            break;
          case 'email':
            error = validateEmail(value);
            break;
          case 'date_of_birth':
            error = validateDateOfBirth(value);
            break;
          case 'password':
            error = validatePassword(value);
            break;
          case 'iban':
            error = validateIBAN(value);
            break;
          case 'destination_id':
            error = validateDestinationID(value);
            break;
          case 'vat':
            error = validateVAT(value);
            break;
          case 'zip':
          case 'invoice_zip':
            error = validateZip(value);
            break;
          default:
            break;
        }
      }
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      return newForm;
    });
  };

  useEffect(() => {
    console.log('terms_and_conditions:', form.terms_and_conditions); // Debug
    const isPersonalInfoComplete =
      form.first_name &&
      form.last_name &&
      form.phone &&
      form.cf &&
      form.email &&
      form.date_of_birth &&
      form.password &&
      form.province_id &&
      form.city_id &&
      form.zip &&
      form.street_name &&
      form.street_number &&
      !Object.values(errors).some((error) => error);

    const isSubjectSelectionComplete =
      form.subjects.length > 0 &&
      form.subjects.some((subject) => subject.schools.some((school) => school.classes.length > 0));

    const isInvoiceInfoComplete =
      form.vat &&
      form.vat_type &&
      form.destination_id &&
      form.iban &&
      form.invoice_province_id &&
      form.invoice_city_id &&
      form.invoice_zip &&
      form.invoice_street_address &&
      form.invoice_street_number &&
      form.terms_and_conditions === 1 &&
      !Object.values(errors).some((error) => error);

    if (step === 'personalInfo') {
      setIsDisabled(!isPersonalInfoComplete);
    } else if (step === 'subjectSelection') {
      setIsDisabled(!isSubjectSelectionComplete);
    } else if (step === 'invoiceInfo') {
      setIsDisabled(!isInvoiceInfoComplete);
    }
  }, [form, errors, step]);

  const manageStep = async () => {
    if (step === 'personalInfo') {
      setStep('subjectSelection');
    } else if (step === 'subjectSelection') {
      setStep('invoiceInfo');
    } else {
      console.log('form:', form);
      const formattedForm = {
        ...form,
        phone: form.phone.startsWith('+39') ? form.phone : `+39${form.phone}`,
      };

      console.log('form inviato:', formattedForm);
      try {
        const response = await register(formattedForm, 'teacher');
        if (response.status === 200) {
          // Registrazione completata con successo, reindirizza al login
          navigate('/login');
        }
      } catch (error) {
        console.error('Errore durante la registrazione:', error);
      }
    }
  };

  const back = () => {
    if (step === 'subjectSelection') {
      setStep('personalInfo');
    } else if (step === 'invoiceInfo') {
      setStep('subjectSelection');
    }
  };

  const onUpdateSubjectsAndSchools = (subjects: SubjectsAndSchools[]) => {
    console.log('subject e scuole nel padre:', subjects);
    setForm((prev) => ({ ...prev, subjects: subjects }));
    console.log('form:', form);
  };

  return (
    <div className="flex flex-col mx-auto gap-y-4 sm:max-w-2xl bg-white p-4 md:p-6 shadow-md rounded-lg">
      <h1 className="text-center text-3xl sm:text-2xl font-bold text-fpcred">Registrazione Insegnante</h1>
      {step === 'personalInfo' && (
        <TeacherPersonalInfo
          form={form}
          provinces={provinces}
          cities={cities}
          handleChange={handleChange}
          errors={errors}
        />
      )}
      {step === 'subjectSelection' && (
        <TeacherSujectRegistration
          form={form}
          onUpdateSubjectsAndSchools={onUpdateSubjectsAndSchools}
          initialSubjectsAndSchools={form.subjects}
          setHideButtons={setHideButtons} // Passa la funzione per nascondere
        />
      )}
      {step === 'invoiceInfo' && <InvoiceInfo form={form} handleChange={handleChange} errors={errors} />}
      <div className={'flex  justify-center w-full gap-4'}>
        {!hideButtons && (step === 'invoiceInfo' || step === 'subjectSelection') && (
          <button className="bg-fpcred px-4 py-2 rounded-lg text-white " onClick={back}>
            Indietro
          </button>
        )}
        {!hideButtons && (
          <button
            disabled={isDisabled}
            className="bg-fpcred px-4 py-2 rounded-lg text-white disabled:bg-gray-500 disabled:cursor-not-allowed"
            onClick={manageStep}>
            {step === 'invoiceInfo' ? 'Registrati' : 'Continua'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TeacherRegistration;
